import * as React from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { Box, Chip, InputAdornment } from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import GetIcon from "@/components/font-awesome-icon";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import { useSelector } from "react-redux";
import { getTagColors } from "@/store/slices/general/dropdownOptionsSlice";
import TruncateTooltip from "@/components/truncate-with-tooltip";

const UISelect = ({
  name,
  value,
  error,
  title,
  onChange,
  onBlur = () => console.log("blur"),
  options = [],
  size = "small",
  clearable = true,
  clearableLabel = "None",
  placeholderOnly = false,
  disabled = false,
  multiple = false,
  tooltip = "",
  optionTooltip = "",
  helperTextStyles = {},
  sx = {},
  selectedSlotLabel = "",
  noDataText = "No option available",
  showNoData = false,
  menuSX = {},
  loading = false,
  wrapperClass = null,
  menuItemSx = {},
  renderChip,
  renderMenuChip,
}) => {
  const tagColors = useSelector(getTagColors);
  const renderValue = (selected) => {
    if (multiple) {
      if (Array.isArray(selected) && selected.length > 0) {
        const labels = selected
          .map((id) => options.find((opt) => opt.value === id)?.label)
          .filter(Boolean);
        return labels.join(", ");
      }
      return placeholderOnly ? (
        <Typography variant="allVariants" sx={{ color: "#a2a2a2" }}>
          {title}
        </Typography>
      ) : null;
    } else {
      const matchingOption = options.find((val) => val.value === selected);
      if (matchingOption) {
        return renderChip ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {renderChipOption(matchingOption)}
          </Box>
        ) : (
          matchingOption.label
        );
      } else if (selectedSlotLabel) {
        return (
          <Typography variant="allVariants" sx={{ color: "red" }}>
            {selectedSlotLabel}
          </Typography>
        );
      } else if (placeholderOnly) {
        return (
          <Typography variant="allVariants" sx={{ color: "#a2a2a2" }}>
            {title}
          </Typography>
        );
      }
      return null;
    }
  };

  const renderChipOption = (option) => {
    let filterColor = {
      code: "var(--palette-default_bg)",
      textCode: "var(--palette-default)",
    };
    if (option.color) {
      filterColor = tagColors.find((color) => color?.id == option.color);
    }
    return (
      <Chip
        key={option.id}
        label={<TruncateTooltip text={option.label} limit={15} />}
        sx={{
          backgroundColor: `${filterColor?.code}`,
          color: `${filterColor?.textCode}!important`,
          WebkitTextFillColor: "initial",
        }}
      />
    );
  };

  return (
    // <FormControl fullWidth size="small">
    //   <Select
    //     name={name}
    //     value={value}
    //     onChange={onChange}
    //     variant="outlined"
    //     renderValue={(selected) =>
    //       selected ? (
    //         options.find((val) => val.value == selected).label
    //       ) : (
    //         <em>Placeholder</em>
    //       )
    //     }
    //   >
    //     {options &&
    //       options.map((opt, index) => (
    //         <MenuItem key={index} value={opt.value}>
    //           {opt.label}
    //         </MenuItem>
    //       ))}
    //   </Select>
    // </FormControl>
    <>
      <Tooltip title={tooltip} placement="top-end">
        <div className={wrapperClass}>
          <FormControl
            fullWidth
            error={!!error}
            size={size}
            sx={{
              ...sx,
              bgcolor: disabled
                ? "var(--palette-disabled_button)"
                : "var(--palette-grid_table)",
              "& .Mui-focused": {
                color: "var(--palette-grey_text) !important",
              },
            }}
          >
            <InputLabel
              id={`${name}-select`}
              sx={{
                color: disabled
                  ? "var(--palette-disabled_button_text)"
                  : "var(--palette-grey_text)",
              }}
            >
              {!placeholderOnly ? title : null}
            </InputLabel>
            <Select
              sx={{
                "& .MuiSelect-select": {
                  // color: "var(--base-140)", // Set the desired color for the underline here
                  color: disabled
                    ? "var(--palette-disabled_button_text)"
                    : "var(--palette-text)", // Set the desired color for the underline here
                  WebkitTextFillColor: disabled
                    ? "var(--palette-disabled_button_text)"
                    : "var(--palette-text)",
                },
                "& .MuiSelect-outlined": {
                  padding: renderChip && value ? "13px" : "",
                },
                "& .Mui-disabled": {
                  WebkitTextFillColor:
                    "var(--palette-disabled_button_text) !important",
                  color: "var(--palette-disabled_button_text)",
                },
              }}
              disabled={disabled}
              multiple={multiple}
              size={size}
              // labelId={`${name}-select`}
              MenuProps={{
                PaperProps: {
                  className: "custom-scroll",
                  style: {
                    maxHeight: "250px",
                    maxWidth: "300px",
                    boxShadow: "0px 1px 6px #05060F26",
                    ...menuSX,
                  },
                  sx: {
                    "& .MuiMenuItem-root.Mui-selected": {
                      backgroundColor: "transparent !important",
                      color: "var(--palette-secondary_button_text) !important",
                    },
                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: "var(--palette-secondary_button_hover)",
                    },
                    "& .MuiMenuItem-root.Mui-selected:hover": {
                      backgroundColor:
                        "var(--palette-secondary_button_hover) !important",
                    },
                  },
                },
              }}
              id={"txt-livechat-select" + name}
              name={name}
              value={multiple ? value || [] : value}
              onChange={onChange}
              onBlur={onBlur}
              label={!placeholderOnly ? title : null}
              displayEmpty={placeholderOnly ? true : false}
              renderValue={(selected) => renderValue(selected)}
              IconComponent={() => (
                <GetIcon
                  icon={faChevronDown}
                  iconStyles={{
                    marginRight: "0.5rem",
                    fontSize: "0.625rem",
                    color: "var(--palette-grey_text)",
                  }}
                />
              )}
            >
              {clearable ? (
                <MenuItem value="">{clearableLabel}</MenuItem>
              ) : null}
              {loading ? (
                <MenuItem value="">Loading..</MenuItem>
              ) : !!options?.length ? (
                options.map((option, index) =>
                  option.disabled ? (
                    <Tooltip
                      title={option.tooltip || ""}
                      placement="top"
                      arrow
                      key={index}
                    >
                      <div>
                        <MenuItem
                          value={option.value}
                          disabled={option.disabled}
                        >
                          <span>
                            {option.label}&nbsp;{option.helperText}
                          </span>
                        </MenuItem>
                      </div>
                    </Tooltip>
                  ) : (
                    <MenuItem
                      value={option.value}
                      key={index}
                      disabled={option.disabled}
                    >
                      {renderMenuChip ? (
                        renderChipOption(option)
                      ) : (
                        <Box
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            ...menuItemSx,
                          }}
                          title={option.label}
                        >
                          {option.label}&nbsp;&nbsp;{option.helperText}
                        </Box>
                      )}
                    </MenuItem>
                  )
                )
              ) : showNoData ? (
                <MenuItem
                  value={"nothing to show"}
                  key={"NoOption"}
                  sx={{ textAlign: "center" }}
                  disabled={true}
                >
                  {noDataText}
                </MenuItem>
              ) : null}
            </Select>
            <FormHelperText
              sx={{
                position: "absolute",
                bottom: "-20px",
                ...helperTextStyles,
              }}
            >
              {error}
            </FormHelperText>
          </FormControl>
        </div>
      </Tooltip>
    </>
  );
};
export default UISelect;
